/**
 * Client-side API access for the admin SPA. The API lives on its own host
 * (api.<domain>) — admin must call it directly (no same-origin /api proxy).
 * Base derived from the current hostname so one build works everywhere.
 */
import { singleFlight, withRefresh } from "@podecontratar/shared/lib/refresh-client";
import { apiOriginForHost } from "@podecontratar/shared/lib/api-origin";
import { apiPath } from "@podecontratar/shared/lib/api-path";
import { createApiClient } from "@podecontratar/shared/contract/client";

function apiUrl(path: string): string {
  if (typeof window === "undefined") return path.startsWith("/") ? path : `/${path}`;
  const resolved = apiPath(path);
  const base = apiOriginForHost(window.location.hostname);
  // Dev: hit the local worker directly (no Next rewrite for the Vite admin SPA);
  // the dev worker mounts under /api, so keep the prefix.
  if (base === null) return `http://localhost:8788/api${resolved}`;
  return `${base}${resolved}`;
}

/**
 * POST the refresh endpoint. Built once at module scope so every call to
 * `apiFetch` shares one in-flight rotation — see `singleFlight`'s docstring
 * for why a per-call instance would collapse nothing and get the user's
 * token family revoked as replay.
 */

/**
 * Deadline for the renewal POST itself.
 *
 * Without this the refresh is unbounded: in the shared module the caller's
 * `timeoutMs` guards the two request attempts but not the renewal between
 * them, and in the two apiFetch layers there is no ambient deadline at all.
 * A stalled /auth/refresh would hang the request that triggered it.
 */
const REFRESH_TIMEOUT_MS = 10_000;

const refreshSession = singleFlight(async (): Promise<boolean> => {
  try {
    const r = await fetch(apiUrl("/api/auth/refresh"), {
      method: "POST",
      credentials: "include",
      signal: AbortSignal.timeout(REFRESH_TIMEOUT_MS),
    });
    return r.ok;
  } catch {
    return false;
  }
});

/**
 * On a 401, transparently renews the session once and retries. Never wraps
 * the refresh call itself — a 401 there means the session is genuinely gone.
 */
export function apiFetch(path: string, init: RequestInit = {}): Promise<Response> {
  return withRefresh(
    () => fetch(apiUrl(path), { credentials: "include", ...init }),
    refreshSession
  );
}

/** Typed client for the API contract, built over `apiFetch` above. */
export const api = createApiClient(apiFetch);
